import type {RevaLanguage} from "../i18n";
import {selectByLanguage} from "../i18n";
import {countsToSectionIISum, SectionIICategory, type SectionIIEntryResult} from "../register";

// ---------------------------------------------------------------------------
// Output types (rendered indication-table rows)
// ---------------------------------------------------------------------------

export interface IndicationRowFormats {
    bold?: boolean | null;
    lineAbove?: boolean | null;
    blankLineAbove?: boolean | null;
    grayedOut?: boolean | null;
}

export interface GroupTitleWithInitiallyUnfold {
    title: string;
    groupKey: string;
    unfoldInitially: boolean;
}

export interface ValueWithDetails {
    value: string | undefined;
    originalValue: string | null;
    tooltip: string | undefined;
    inputApiParam: string | null;
    groupTitle: string;
    /** The api-param key the cell's value was read from (`IndicationSpecOutputRef.field`). */
    field: string;
    /** Rendered "low"/"high" companion values (`IndicationSpecOutputRef.lowApiParam`/`highApiParam`), e.g. for a value range/span. Null unless both are configured and present. */
    low: string | null;
    high: string | null;
}

export interface ValueIndicationDataRow {
    group: GroupTitleWithInitiallyUnfold;
    title: string;
    formats?: IndicationRowFormats;
    factor?: ValueWithDetails | null;
    market_value_factor?: ValueWithDetails | null;
    market_value?: ValueWithDetails | null;
    lending_value_factor?: ValueWithDetails | null;
    lending_value?: ValueWithDetails | null;
}

// ---------------------------------------------------------------------------
// Input types (spec, as provided by each caller)
//
// Field names use the "De"/"En" suffix convention of the raw AVM spec API
// (matches reva-pdf's own spec types 1:1). Callers whose internal spec
// representation uses a different casing (e.g. the client's "DE"/"EN")
// adapt at the call site.
// ---------------------------------------------------------------------------

export interface IndicationSpecOutputRef {
    field: string;
    field_is_input: boolean;
    format?: string;
    unitDe?: string | null;
    unitEn?: string | null;
    descriptionDe?: string | null;
    descriptionEn?: string | null;
    hideIfEmpty?: boolean;
    valueIfEmpty?: string | null;
    associatedInputApiParam?: string | null;
    /** Opaque to the aggregation logic; forwarded to the injected {@link FormatCell} callback. */
    categories?: unknown;
    /** Inline value carried by a synthetic cell built for an array-expanded row (SPF/Abteilung II). */
    value?: unknown;
    /** Api-param key of a "low" companion value (e.g. for a value range/span), formatted with `lowFormat` (else like this cell's main value). */
    lowApiParam?: string | null;
    /** Api-param key of a "high" companion value (e.g. for a value range/span), formatted with `highFormat` (else like this cell's main value). */
    highApiParam?: string | null;
    /** Own display format of the "low" companion value, e.g. `DOUBLE(0,<)` to round the span's lower end down (#19096). */
    lowFormat?: string;
    /** Own display format of the "high" companion value, e.g. `DOUBLE(0,>)` to round the span's upper end up (#19096). */
    highFormat?: string;
}

export interface IndicationSpecOutputRow {
    group: string;
    titleDe?: string;
    titleEn?: string;
    formats?: IndicationRowFormats;
    hideUnlessApiParametersExisting?: string[] | null;
    factor?: IndicationSpecOutputRef | null;
    market_value_factor?: IndicationSpecOutputRef | null;
    market_value?: IndicationSpecOutputRef | null;
    lending_value_factor?: IndicationSpecOutputRef | null;
    lending_value?: IndicationSpecOutputRef | null;
}

export interface IndicationSpecOutputRowGroup {
    group: string;
    headerDe: string;
    headerEn: string;
    hideUnlessApiParametersExisting?: string[] | null;
    unfoldInitially?: boolean;
}

export interface IndicationSpecOutputPage {
    columns: {field: string}[];
    rowGroups: IndicationSpecOutputRowGroup[];
    rows: IndicationSpecOutputRow[];
}

export interface IndicationSpec {
    pages: Record<string, IndicationSpecOutputPage>;
}

/** Structural shape shared by both callers' `IndicationParameterDetails`. */
export interface IndicationParameterDetailsLike {
    input: Record<string, unknown>;
    output: Record<string, unknown>;
    combined: Record<string, unknown>;
}

/** Renders a resolved cell value to its display string; delegates to the caller's own number-formatting. `omitUnit` is used for the "low" end of a low/high range, so the unit is only shown once (on "high"). */
export type FormatCell = (
    lang: RevaLanguage,
    value: unknown,
    cell: IndicationSpecOutputRef,
    omitUnit?: boolean,
) => string;

/** Optionally computes the non-rounded "original value" shown alongside a rendered cell (client-only feature). */
export type ComputeOriginalValue = (
    cell: IndicationSpecOutputRef,
    renderedValue: string,
    lang: RevaLanguage,
    indicationDetails: IndicationParameterDetailsLike,
) => string | null;

// ---------------------------------------------------------------------------
// Internal working types
//
// During aggregation the spec output rows are enriched with synthetic rows
// (special property features / Abteilung II entries) whose cells carry an
// inline `value` and only fill some of the columns — hence the loose shapes.
// ---------------------------------------------------------------------------

interface AvmSpecialPropertyFeatureDetails {
    feature_de?: string | null;
    feature_en?: string | null;
    type?: "discount" | "premium";
    value: number;
}

type AvmSpecialPropertyFeatureList = Record<string, AvmSpecialPropertyFeatureDetails[] | undefined>;

/**
 * Create synthetic indication table rows for Special Property Features ("Zu/Abschläge besondere Objektmerkmale").
 */
function createSyntheticIndicationRowsSPF(
    featureList: AvmSpecialPropertyFeatureList,
    group: string,
    featureCell: IndicationSpecOutputRef,
    hasSpfLending: boolean,
): IndicationSpecOutputRow[] {
    const marketField = "special_property_feature_list";
    const lendingField = `${marketField}_lending`;
    const rows: IndicationSpecOutputRow[] = [];
    const marketFeatures = featureList?.[marketField] || [];
    const lendingFeatures = hasSpfLending ? [...(featureList?.[lendingField] || [])] : [];

    const marketCell: IndicationSpecOutputRef = {
        field: marketField,
        field_is_input: false,
        unitDe: featureCell.unitDe,
        unitEn: featureCell.unitEn,
        format: featureCell.format,
    };
    const lendingCell: IndicationSpecOutputRef = {...marketCell, field: lendingField};

    const createRow = (
        feature: AvmSpecialPropertyFeatureDetails,
        lendingValue: number | null,
    ): IndicationSpecOutputRow => ({
        titleDe: ` ${feature.feature_de || ""}`,
        titleEn: ` ${feature.feature_de || ""}`,
        group,
        formats: {grayedOut: true},
        lending_value: lendingValue != null ? {...lendingCell, value: lendingValue} : null,
    });

    marketFeatures.forEach((feature) => {
        const lendingIndex = lendingFeatures.findIndex((f) => f.feature_de === feature.feature_de);
        let lendingValue: number | null = null;
        if (lendingIndex !== -1) {
            lendingValue = lendingFeatures[lendingIndex].value;
            lendingFeatures.splice(lendingIndex, 1);
        }
        rows.push({
            ...createRow(feature, lendingValue),
            market_value: {...marketCell, value: feature.value},
        });
    });
    lendingFeatures.forEach((feature) => {
        rows.push(createRow(feature, feature.value));
    });

    return rows;
}

/**
 * Create table rows for Special Property Features.
 */
function createSpecialFeatureRows(
    rows: IndicationSpecOutputRow[],
    group: IndicationSpecOutputRowGroup,
    indicationDetails: IndicationParameterDetailsLike,
): {spfIndex?: number; spfRows?: IndicationSpecOutputRow[]} {
    const spfIndex = rows.findIndex((row) => row.market_value?.field === "special_properties");
    if (spfIndex !== -1) {
        const hasSpfLending = indicationDetails.output["special_properties_lending"] != null;
        const spfList = indicationDetails.output["special_property_feature_list"];
        if (spfList && Array.isArray(spfList) && spfList.length > 0) {
            const spfRows = createSyntheticIndicationRowsSPF(
                spfList[0],
                group.group,
                rows[spfIndex].market_value as IndicationSpecOutputRef,
                hasSpfLending,
            );
            return {spfIndex, spfRows};
        }
    }
    return {};
}

/**
 * Create synthetic indication table rows for section_ii_entries ("Lasten Abteilung II").
 */
function createSyntheticIndicationRowsAbt2(
    featureList: SectionIIEntryResult[],
    group: string,
    featureCell: IndicationSpecOutputRef,
    sumLendingCell: IndicationSpecOutputRef | null | undefined,
): IndicationSpecOutputRow[] {
    const marketField = "section_ii_entries";

    const marketCell: IndicationSpecOutputRef = {
        field: marketField,
        field_is_input: false,
        unitDe: featureCell.unitDe,
        unitEn: featureCell.unitEn,
        format: featureCell.format,
    };

    const lendingCell: IndicationSpecOutputRef | null = sumLendingCell
        ? {
              ...marketCell,
              field: `${marketField}_lending`,
              unitDe: sumLendingCell.unitDe,
              unitEn: sumLendingCell.unitEn,
              format: sumLendingCell.format,
          }
        : null;
    return featureList
        .map((feature) => ({
            feature,
            market: countsToSectionIISum("market", feature) && feature.value ? feature.value : null,
            lending:
                lendingCell && countsToSectionIISum("lending", feature) && feature.value_lending
                    ? feature.value_lending
                    : null,
        }))
        .filter(({market, lending}) => market != null || lending != null)
        .map(({feature, market, lending}) => ({
            titleDe: ` ${feature.subtype_de || ""}`,
            titleEn: ` ${feature.subtype_de || ""}`,
            group,
            formats: {grayedOut: true},
            market_value: market != null ? {...marketCell, value: market} : null,
            lending_value: lendingCell && lending != null ? {...lendingCell, value: lending} : null,
        }));
}

/**
 * Create table rows for section_ii_entries.
 */
function createSectionIIRows(
    rows: IndicationSpecOutputRow[],
    group: IndicationSpecOutputRowGroup,
    indicationDetails: IndicationParameterDetailsLike,
): {abt2Index?: number; abt2Rows?: IndicationSpecOutputRow[]} {
    const abt2Index = rows.findIndex((row) => row.market_value?.field === "section_ii_sum");
    if (abt2Index !== -1) {
        const abt2List = indicationDetails.output["section_ii_entries"];
        if (abt2List && Array.isArray(abt2List) && abt2List.length > 0) {
            const abt2Rows = createSyntheticIndicationRowsAbt2(
                abt2List,
                group.group,
                rows[abt2Index].market_value as IndicationSpecOutputRef,
                rows[abt2Index].lending_value,
            );
            return {abt2Index, abt2Rows};
        }
    }
    return {};
}

/**
 * Block „Noch zu berücksichtigende Vorlasten“ (#19602-27): Summe und einzelne Vorlasten nur in der Spalte
 * Beleihungswert. Sie gehen nicht in den Wert ein und stehen deshalb unter dem Ergebnis des Verfahrens.
 */
function createPriorChargeRows(
    featureList: SectionIIEntryResult[],
    group: string,
    sumLendingCell: IndicationSpecOutputRef,
): IndicationSpecOutputRow[] {
    const priorCharges = featureList.filter(
        (entry) => entry.category_lending === SectionIICategory.PRIOR_CHARGE && entry.value_lending != null,
    );
    if (priorCharges.length === 0) return [];

    const sumCell: IndicationSpecOutputRef = {
        field: "section_ii_prior_charges_lending",
        field_is_input: false,
        unitDe: sumLendingCell.unitDe,
        unitEn: sumLendingCell.unitEn,
        format: sumLendingCell.format,
        hideIfEmpty: true,
    };
    return [
        {
            titleDe: "Noch zu berücksichtigende Vorlasten",
            titleEn: "Prior charges still to be considered",
            group,
            formats: {lineAbove: true},
            lending_value: sumCell,
        },
        ...priorCharges.map((entry) => ({
            titleDe: ` ${entry.subtype_de || ""}`,
            titleEn: ` ${entry.subtype_de || ""}`,
            group,
            formats: {grayedOut: true},
            lending_value: {
                ...sumCell,
                field: "section_ii_entries_lending",
                hideIfEmpty: false,
                value: entry.value_lending,
            },
        })),
    ];
}

/** Index der Zeile „… pro m²“ hinter den Lasten Abt. II, sonst die letzte Zeile. */
function priorChargeInsertIndex(rows: IndicationSpecOutputRow[], abt2Index: number): number {
    const perArea = rows.findIndex(
        (row, index) => index > abt2Index && /_per_(sqm|area)$/.test(row.market_value?.field ?? ""),
    );
    return perArea !== -1 ? perArea : rows.length - 1;
}

function enrichRowsWithArrayParameter(
    rows: IndicationSpecOutputRow[],
    group: IndicationSpecOutputRowGroup,
    indicationDetails: IndicationParameterDetailsLike,
): IndicationSpecOutputRow[] {
    const {spfIndex, spfRows} = createSpecialFeatureRows(rows, group, indicationDetails);
    if (spfIndex != null) {
        rows.splice(spfIndex + 1, 0, ...(spfRows ?? []));
    }
    const {abt2Index, abt2Rows} = createSectionIIRows(rows, group, indicationDetails);
    if (abt2Index != null) {
        rows.splice(abt2Index + 1, 0, ...(abt2Rows ?? []));
        const sumLendingCell = rows[abt2Index].lending_value;
        const abt2List = indicationDetails.output["section_ii_entries"] as SectionIIEntryResult[];
        if (sumLendingCell && indicationDetails.output["section_ii_prior_charges_lending"] != null) {
            const priorChargeRows = createPriorChargeRows(abt2List, group.group, sumLendingCell);
            rows.splice(priorChargeInsertIndex(rows, abt2Index) + 1, 0, ...priorChargeRows);
        }
    }
    return rows;
}

const ROUNDING_FIELDS = ["value_raw", "value_roundgap"];

/** #18955: unrounded value and rounding gap only add noise when nothing was rounded (gap 0 or missing). */
function isUnusedRoundingRow(row: IndicationSpecOutputRow, indicationDetails: IndicationParameterDetailsLike): boolean {
    const {value_roundgap, value_lending_roundgap} = indicationDetails.output;
    return ROUNDING_FIELDS.includes(row.market_value?.field ?? "") && !value_roundgap && !value_lending_roundgap;
}

function hasDataToShow(
    row: IndicationSpecOutputRow,
    pageSpec: IndicationSpecOutputPage,
    indicationDetails: IndicationParameterDetailsLike,
): boolean {
    if (isUnusedRoundingRow(row, indicationDetails)) return false;
    if (
        Array.isArray(row.hideUnlessApiParametersExisting) &&
        row.hideUnlessApiParametersExisting.length > 0 &&
        !row.hideUnlessApiParametersExisting.some(
            (param) => Object.hasOwn(indicationDetails.combined, param) && indicationDetails.combined[param] !== null,
        )
    ) {
        return false;
    }

    return pageSpec.columns.some((col) => {
        const cell = (row as unknown as Record<string, IndicationSpecOutputRef | null | undefined>)[col.field];
        if (!cell) {
            return false;
        }

        const targetParams = cell.field_is_input ? indicationDetails.input : indicationDetails.output;
        if (!targetParams) {
            return false;
        }
        return (Object.hasOwn(targetParams, cell.field) && targetParams[cell.field] !== null) || !cell.hideIfEmpty;
    });
}

function getRenderedValue(
    cell: IndicationSpecOutputRef,
    lang: RevaLanguage,
    indicationDetails: IndicationParameterDetailsLike,
    formatCell: FormatCell,
): string | undefined {
    const value = cell.value;
    if (value != null) {
        return formatCell(lang, value, cell);
    }
    const targetParams = cell.field_is_input ? indicationDetails.input : indicationDetails.output;
    if (Object.hasOwn(targetParams, cell.field) && targetParams[cell.field] !== null) {
        return formatCell(lang, targetParams[cell.field], cell);
    } else if (typeof cell.valueIfEmpty === "string") {
        return cell.valueIfEmpty;
    } else if (!cell.hideIfEmpty) {
        return formatCell(lang, 0.0, cell);
    }
    return undefined;
}

/**
 * Resolve and format a cell's "low"/"high" companion value (same target params as the cell's main value;
 * its own format if given, else the main value's), or null if the companion field isn't configured or has
 * no value.
 */
function resolveCompanionValue(
    companionField: string | null | undefined,
    companionFormat: string | undefined,
    cell: IndicationSpecOutputRef,
    lang: RevaLanguage,
    indicationDetails: IndicationParameterDetailsLike,
    formatCell: FormatCell,
    omitUnit: boolean,
): string | null {
    if (!companionField) return null;
    const targetParams = cell.field_is_input ? indicationDetails.input : indicationDetails.output;
    if (!targetParams || !Object.hasOwn(targetParams, companionField) || targetParams[companionField] == null) {
        return null;
    }
    const formatRef = companionFormat ? {...cell, format: companionFormat} : cell;
    return formatCell(lang, targetParams[companionField], formatRef, omitUnit);
}

function createCellData(
    cell: IndicationSpecOutputRef,
    renderedValue: string,
    lang: RevaLanguage,
    groupTitle: string,
    indicationDetails: IndicationParameterDetailsLike,
    formatCell: FormatCell,
    computeOriginalValue?: ComputeOriginalValue,
): ValueWithDetails {
    const description = selectByLanguage(lang, cell.descriptionDe, cell.descriptionEn) ?? undefined;
    let inputApiParam: string | null | undefined = cell.field_is_input ? cell.field : cell.associatedInputApiParam;
    if (cell.field.startsWith("special_properties")) {
        inputApiParam = null;
    }

    return {
        value: renderedValue,
        originalValue: computeOriginalValue?.(cell, renderedValue, lang, indicationDetails) ?? null,
        tooltip: description,
        inputApiParam: inputApiParam ?? null,
        groupTitle,
        field: cell.field,
        // Unit shown only once, on the upper bound.
        low: resolveCompanionValue(cell.lowApiParam, cell.lowFormat, cell, lang, indicationDetails, formatCell, true),
        high: resolveCompanionValue(
            cell.highApiParam,
            cell.highFormat,
            cell,
            lang,
            indicationDetails,
            formatCell,
            false,
        ),
    };
}

function transformToIndicationRow(
    row: IndicationSpecOutputRow,
    group: IndicationSpecOutputRowGroup,
    groupTitle: string,
    pageSpec: IndicationSpecOutputPage,
    lang: RevaLanguage,
    indicationDetails: IndicationParameterDetailsLike,
    formatCell: FormatCell,
    computeOriginalValue?: ComputeOriginalValue,
): ValueIndicationDataRow {
    const result: ValueIndicationDataRow = {
        group: {title: groupTitle, groupKey: group.group, unfoldInitially: group.unfoldInitially ?? false},
        title: selectByLanguage(lang, row.titleDe ?? "", row.titleEn ?? ""),
        formats: row.formats,
    };

    for (const col of pageSpec.columns) {
        const cell = (row as unknown as Record<string, IndicationSpecOutputRef | null | undefined>)[col.field];
        if (!cell) {
            continue;
        }

        const renderedValue = getRenderedValue(cell, lang, indicationDetails, formatCell);
        if (renderedValue !== undefined) {
            (result as unknown as Record<string, unknown>)[col.field] = createCellData(
                cell,
                renderedValue,
                lang,
                groupTitle,
                indicationDetails,
                formatCell,
                computeOriginalValue,
            );
        }
    }

    return result;
}

const LOWEST_VALUE_PAIR = ["value_primary", "value_primary_lending"];

/**
 * #19568: Markt- und Beleihungswert vor Niederstwert kamen aus der AVM unter vertauschten Namen. Je nach
 * AVM-Stand (LIVE hängt bis zu einem Release hinterher, siehe AVM-KOMPATIBILITAET.md) und Alter eines
 * eingefrorenen Snapshots passen Spec und Antwort nicht zusammen. Der Beleihungswert ist nie größer als der
 * Marktwert, daher wird das Paar an der Zeile ausgerichtet, die es anzeigt.
 */
export function alignLowestValuePair(
    rows: IndicationSpecOutputRow[] | undefined,
    indicationDetails: IndicationParameterDetailsLike,
): IndicationParameterDetailsLike {
    const row = rows?.find((r) => {
        const fields = [r.market_value?.field, r.lending_value?.field];
        return LOWEST_VALUE_PAIR.every((f) => fields.includes(f));
    });
    const marketField = row?.market_value?.field;
    const lendingField = row?.lending_value?.field;
    if (!marketField || !lendingField) return indicationDetails;

    const market = indicationDetails.output[marketField];
    const lending = indicationDetails.output[lendingField];
    if (typeof market !== "number" || typeof lending !== "number" || market >= lending) return indicationDetails;

    const swapped = {[marketField]: lending, [lendingField]: market};
    return {
        ...indicationDetails,
        output: {...indicationDetails.output, ...swapped},
        combined: {...indicationDetails.combined, ...swapped},
    };
}

/**
 * Process multiple row groups for the indication table data.
 *
 * Lower-level entry point that takes an explicit list of row groups instead of deriving it from the
 * spec's `hideUnlessApiParametersExisting` filter — used directly by callers building synthetic pages
 * (e.g. appending the VALUE page's konvergenz rows) alongside the derived {@link aggregateIndicationTableData}.
 */
export function processIndicationRowGroups(
    lang: RevaLanguage,
    spec: IndicationSpec,
    endpoint: string,
    indicationDetails: IndicationParameterDetailsLike | null,
    groups: IndicationSpecOutputRowGroup[],
    formatCell: FormatCell,
    computeOriginalValue?: ComputeOriginalValue,
): ValueIndicationDataRow[] {
    if (!indicationDetails) return [];
    indicationDetails = alignLowestValuePair(spec.pages[endpoint]?.rows, indicationDetails);

    return groups.flatMap((group) => {
        const pageSpec = Object.hasOwn(spec.pages, endpoint) ? spec.pages[endpoint] : null;
        if (
            !pageSpec ||
            !Array.isArray(pageSpec.rowGroups) ||
            !Array.isArray(pageSpec.rows) ||
            !Array.isArray(pageSpec.columns)
        ) {
            console.warn("invalid call to processIndicationRowGroups()", {lang, endpoint, group});
            return [];
        }

        const groupTitle = selectByLanguage(lang, group.headerDe, group.headerEn);
        const rows = pageSpec.rows.filter((row) => row.group === group.group);
        // Clone rows to avoid mutating the spec.
        const rowsClone = JSON.parse(JSON.stringify(rows)) as IndicationSpecOutputRow[];
        const enrichedRows = enrichRowsWithArrayParameter(rowsClone, group, indicationDetails);
        return enrichedRows
            .filter((row) => hasDataToShow(row, pageSpec, indicationDetails))
            .map((row) =>
                transformToIndicationRow(
                    row,
                    group,
                    groupTitle,
                    pageSpec,
                    lang,
                    indicationDetails,
                    formatCell,
                    computeOriginalValue,
                ),
            );
    });
}

/**
 * Compute and aggregate the data for indication details to the row-data to display it as a table.
 *
 * Derives the row groups to render from the spec's own `hideUnlessApiParametersExisting` filter; for
 * a curated/synthetic set of groups use {@link processIndicationRowGroups} directly.
 */
export function aggregateIndicationTableData(
    lang: RevaLanguage,
    spec: IndicationSpec | null | undefined,
    endpoint: string,
    indicationDetails: IndicationParameterDetailsLike | null,
    formatCell: FormatCell,
    computeOriginalValue?: ComputeOriginalValue,
): ValueIndicationDataRow[] {
    if (!endpoint || !spec?.pages?.[endpoint]) {
        console.warn("invalid call to aggregateIndicationTableData()", {lang, endpoint});
        return [];
    }
    const pageSpec = spec.pages[endpoint];
    if (!Array.isArray(pageSpec.rowGroups) || !Array.isArray(pageSpec.rows) || !Array.isArray(pageSpec.columns)) {
        console.warn("invalid call to aggregateIndicationTableData()", {lang, endpoint});
        return [];
    }
    if (!indicationDetails) return [];

    const filteredGroups = pageSpec.rowGroups.filter((group) => {
        if (Array.isArray(group.hideUnlessApiParametersExisting) && group.hideUnlessApiParametersExisting.length > 0) {
            return group.hideUnlessApiParametersExisting.some(
                (param) =>
                    Object.hasOwn(indicationDetails.combined, param) && indicationDetails.combined[param] !== null,
            );
        }
        return true;
    });

    return processIndicationRowGroups(
        lang,
        spec,
        endpoint,
        indicationDetails,
        filteredGroups,
        formatCell,
        computeOriginalValue,
    );
}

/** Loosely-typed read view of an IndicationParameterDetails.combined record (dynamic AVM JSON). */
type CombinedIndicationValues = Record<string, number | string | null | undefined>;

interface IndicationAppraisalCategory {
    categoryNumber?: number | string | null;
    shortNameDe?: string | null;
    shortNameEn?: string | null;
}

/**
 * Look up the indication titles (DE/EN) for the given indication data via the spec output params.
 */
export function getIndicationName(
    indicationData: CombinedIndicationValues | null | undefined,
    outputParams: Record<string, unknown> | null | undefined,
): {indicationNameDe: string | null; indicationNameEn: string | null} {
    let indicationNameDe: string | null = null;
    let indicationNameEn: string | null = null;
    const categories = (outputParams?.indication_appraisal as {categories?: IndicationAppraisalCategory[]} | undefined)
        ?.categories;
    const categoryNumber = indicationData?.indication_appraisal;

    if (indicationData?.indication != null && categoryNumber != null && Array.isArray(categories)) {
        const category = categories.find((cat) => cat.categoryNumber === categoryNumber);

        if (category) {
            indicationNameDe = category.shortNameDe ?? null;
            indicationNameEn = category.shortNameEn ?? null;
        }
    }

    return {indicationNameDe, indicationNameEn};
}
